import {Switch} from '@wandb/weave/components';
import {Select} from '@wandb/weave/components/Form/Select';
import {TextField} from '@wandb/weave/components/Form/TextField';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React from 'react';
import {create} from 'zustand';
import {useShallow} from 'zustand/react/shallow';

import {EventTriggeringConditionType} from '../TriggerEventTypes';
import {Trigger} from '../TriggersTable/utilTypes';

// only AVERAGE is supported for now
export type MetricFilterAggOp = 'AVERAGE';

export type CmpOp = '$gt' | '$gte' | '$lt' | '$lte' | '$ne' | '$eq'; // TODO: add more + add UI nice string for these

export type CmpOpOption = {
  value: CmpOp;
  label: string;
};

export const cmpOpOptions: CmpOpOption[] = [
  {value: '$gt', label: 'Above'},
  {value: '$gte', label: 'Above or Equal to'},
  {value: '$lt', label: 'Below'},
  {value: '$lte', label: 'Below or Equal to'},
  {value: '$ne', label: 'Not Equal to'},
  {value: '$eq', label: 'Equal to'},
];

export type MetricFilter = {
  metricName: string;
  threshold: string;
  windowSize: string;
  aggOp: MetricFilterAggOp;
  cmpOp: CmpOpOption;
};

export type RunFilter = {
  limitToMyRuns: boolean;
  isRunNameFilterEnabled: boolean;
  runName?: string;
  username: string;
};

export type RunMetricThresholdConfig = {
  metricFilter: Partial<MetricFilter> &
    Pick<MetricFilter, 'cmpOp' | 'windowSize' | 'aggOp'>;
  runFilter: Partial<RunFilter> & Pick<RunFilter, 'isRunNameFilterEnabled'>;
  allowAutoFocus: boolean;
};

const DEFAULT_WINDOW_SIZE = 5;

const initialRunMetricConfig: RunMetricThresholdConfig = {
  metricFilter: {
    metricName: undefined,
    threshold: undefined,
    windowSize: DEFAULT_WINDOW_SIZE.toString(),
    cmpOp: cmpOpOptions[0],
    aggOp: 'AVERAGE',
  },
  runFilter: {
    limitToMyRuns: false,
    isRunNameFilterEnabled: false,
    runName: undefined,
  },
  allowAutoFocus: true,
};

export const useRunMetricThresholdStore = create<RunMetricThresholdConfig>()(
  () => ({
    ...initialRunMetricConfig,
  })
);

export const RunMetricThresholdActions = {
  initAsync: async (suggestedMetricName?: string) => {
    useRunMetricThresholdStore.setState({
      ...initialRunMetricConfig,
      metricFilter: {
        ...initialRunMetricConfig.metricFilter,
        metricName: suggestedMetricName,
      },
      allowAutoFocus: suggestedMetricName == null,
    });
  },

  setMetricName: (metricName: string) => {
    setMetricFilter({metricName});
  },
  setThreshold: (threshold: string) => {
    setMetricFilter({threshold});
  },
  setCmpOp: (cmpOpOption: CmpOpOption) => {
    setMetricFilter({cmpOp: cmpOpOption});
  },
  setRunName: (runName: string) => {
    setRunFilter({runName});
  },
  setWindowSize: (windowSize: string) => {
    setMetricFilter({windowSize});
  },
  setIsRunNameFilterEnabled: (isRunNameFilterEnabled: boolean) => {
    setRunFilter({isRunNameFilterEnabled});
  },
};

const setMetricFilter = (metricFilter: Partial<MetricFilter>) => {
  useRunMetricThresholdStore.setState(state => ({
    metricFilter: {...state.metricFilter, ...metricFilter},
  }));
};

const setRunFilter = (runFilter: Partial<RunFilter>) => {
  useRunMetricThresholdStore.setState(state => ({
    runFilter: {...state.runFilter, ...runFilter},
  }));
};

export const isValidMetricName = (metricName: string | undefined) => {
  return metricName !== undefined && metricName.trim() !== '';
};

export const getNumberValueForField = (field: string | undefined) => {
  const fieldNumber = Number(field || '');
  return fieldNumber;
};

export const isValidThreshold = (threshold: string | undefined) => {
  if (threshold === undefined || threshold === '') {
    return false;
  }
  const thresholdNumber = getNumberValueForField(threshold);
  return !isNaN(thresholdNumber) && isFinite(thresholdNumber);
};

export const isValidRunName = (
  isRunNameFilterEnabled: boolean,
  runName: string | undefined
) => {
  return !isRunNameFilterEnabled || (runName !== undefined && runName !== '');
};

export const isValidWindowSize = (windowSize: string | undefined) => {
  if (windowSize === undefined || windowSize === '') {
    return false;
  }
  const windowSizeNumber = getNumberValueForField(windowSize);
  return (
    Number.isInteger(windowSizeNumber) &&
    windowSizeNumber >= 1 &&
    windowSizeNumber <= 100
  );
};

export const isValidRunMetricThresholdConfig = (
  config: RunMetricThresholdConfig
) => {
  return (
    isValidMetricName(config.metricFilter.metricName) &&
    isValidThreshold(config.metricFilter.threshold) &&
    isValidWindowSize(config.metricFilter.windowSize) &&
    // cmpOp is asserted to always be valid by types
    // aggOp is currently hardcoded. For the futurenote that undefined is a valid
    //   value for aggOp - it means "take the last value and ignore windowSize"
    //   which is not something the UI currently supports, but it's not an error
    isValidRunName(
      config.runFilter.isRunNameFilterEnabled,
      config.runFilter.runName
    )
  );
};

export const getEventConfigForRunMetric = (
  config: RunMetricThresholdConfig
) => {
  return {
    eventTriggeringConditionType: EventTriggeringConditionType.RunMetric,
    filters: {
      filters: [
        {
          metricFilter: {...config.metricFilter, aggOp: 'AVERAGE'},
          runFilter: config.runFilter,
        },
      ],
    },
  };
};

const ErrorText = ({children}: {children: React.ReactNode}) => {
  return <div className="mt-1 text-sm leading-5 text-red-550">{children}</div>;
};

const BoldLabel = ({children}: {children: React.ReactNode}) => {
  return <div className="font-semibold">{children}</div>;
};

const SeparatedChunk = ({children}: {children: React.ReactNode}) => {
  return <div className="mt-16">{children}</div>;
};

export const RunMetricThresholdSection = () => {
  const metricFilter = useRunMetricThresholdStore(
    useShallow(state => state.metricFilter)
  );
  const runFilter = useRunMetricThresholdStore(
    useShallow(state => state.runFilter)
  );
  const allowAutoFocus = useRunMetricThresholdStore(
    state => state.allowAutoFocus
  );
  // the undefined checks here prevent showing the error before user has configured that field
  const showMetricNameError =
    metricFilter.metricName !== undefined &&
    !isValidMetricName(metricFilter.metricName);
  const showThresholdError =
    metricFilter.threshold !== undefined &&
    !isValidThreshold(metricFilter.threshold);
  const showRunNameError =
    runFilter.isRunNameFilterEnabled &&
    runFilter.runName !== undefined &&
    !isValidRunName(runFilter.isRunNameFilterEnabled, runFilter.runName);
  const showWindowSizeError =
    metricFilter.windowSize !== undefined &&
    !isValidWindowSize(metricFilter.windowSize);

  return (
    <Tailwind>
      <div className="mt-16 font-semibold">Metric setup</div>
      <div className="mt-8 flex flex-col gap-4 rounded-lg border border-moon-200 p-16">
        <BoldLabel>Metric</BoldLabel>
        <TextField
          value={metricFilter.metricName || ''}
          onChange={RunMetricThresholdActions.setMetricName}
          placeholder="Ex. loss"
          autoFocus={
            /* We don't want to auto focus the metric name field when we're in the workspace drawer -
            it causes jankiness when the drawer opens (the text field is focused and it forces the drawer open
            before the animation is complete). We are using "is the metric name field populated" as a proxy here,
            since it makes sense not to auto focus a field that's already been filled in anyway. */
            allowAutoFocus
          }
          errorState={showMetricNameError}
        />
        {showMetricNameError && (
          <ErrorText>Metric name cannot be empty</ErrorText>
        )}
        <SeparatedChunk>
          <div className="flex items-center">
            Average across{' '}
            <span className="mx-8 inline-block w-64">
              <TextField
                type="number"
                value={metricFilter.windowSize.toString()}
                onChange={value => {
                  RunMetricThresholdActions.setWindowSize(value);
                }}
                placeholder="Ex. 5"
                errorState={showWindowSizeError}
              />
            </span>
            logged values{' '}
            <Tooltip
              content="Integer 1 - 100 required. Average the metric over a set number of logged values before assessing if a certain threshold has been met."
              trigger={
                <Icon className="inline-block text-moon-400" name="info" />
              }
            />
          </div>
          {showWindowSizeError && (
            <ErrorText>Integer 1 - 100 required</ErrorText>
          )}
        </SeparatedChunk>
        <SeparatedChunk>
          <BoldLabel>Metric threshold</BoldLabel>
          <div className="mt-8 flex flex-row gap-16">
            <div className="flex-1">
              <Select<CmpOpOption>
                value={metricFilter.cmpOp}
                onChange={e => RunMetricThresholdActions.setCmpOp(e!)}
                options={cmpOpOptions}
              />
            </div>

            <div className="flex-1">
              <TextField
                value={metricFilter.threshold || ''}
                onChange={value =>
                  RunMetricThresholdActions.setThreshold(value)
                }
                placeholder="Ex. 0.9"
                errorState={showThresholdError}
              />
            </div>
          </div>
          {showThresholdError && (
            <ErrorText>Threshold must be a valid number</ErrorText>
          )}
        </SeparatedChunk>
      </div>
      <SeparatedChunk>
        <div className="flex flex-row">
          <div className="flex-1">
            <BoldLabel>Filter on run name</BoldLabel>
            <span className="text-sm text-moon-500">
              {/* yes, requested ux text is to use the word "trigger" here */}
              Trigger this event only on runs whose name contains the following
              regex string.
            </span>
          </div>
          <div className="flex-0">
            <Switch.Root
              size="medium"
              checked={runFilter.isRunNameFilterEnabled}
              onCheckedChange={isChecked =>
                RunMetricThresholdActions.setIsRunNameFilterEnabled(isChecked)
              }>
              <Switch.Thumb
                size="medium"
                checked={runFilter.isRunNameFilterEnabled}
              />
            </Switch.Root>
          </div>
        </div>
        <div className="mt-8">
          <TextField
            value={
              runFilter.isRunNameFilterEnabled ? runFilter.runName || '' : ''
            }
            onChange={value => RunMetricThresholdActions.setRunName(value)}
            placeholder="Ex. run-name-.*"
            disabled={!runFilter.isRunNameFilterEnabled}
            errorState={showRunNameError}
          />
          {showRunNameError && (
            <ErrorText>Run name cannot be empty if specified</ErrorText>
          )}
        </div>
      </SeparatedChunk>
    </Tailwind>
  );
};

// this is used when we're looking at a already created trigger in the UI
export const getRunMetricThresholdConfig = (
  trigger: Trigger
): RunMetricThresholdConfig | undefined => {
  if (
    !trigger.triggeringCondition.filter ||
    trigger.triggeringCondition.eventType !==
      EventTriggeringConditionType.RunMetric
  ) {
    return undefined;
  }

  try {
    const parsedFilter = JSON.parse(trigger.triggeringCondition.filter);

    const runFilter = JSON.parse(parsedFilter.run_filter);
    if (
      !parsedFilter.metric_filter &&
      !parsedFilter.run_metric_filter?.threshold_filter
    ) {
      console.error('Failed to parse run metric config:', parsedFilter);
      return undefined;
    }
    const rawMetricFilter = parsedFilter.metric_filter
      ? JSON.parse(parsedFilter.metric_filter)
      : parsedFilter.run_metric_filter.threshold_filter;

    // Convert the mongo filter back to our RunFilter type
    const runNameFilter = runFilter.$and?.find(
      (f: any) => f?.run?.displayName?.$regex != null
    );
    const usernameFilter = runFilter.$and?.find(
      (f: any) => f?.run?.username?.$eq != null
    );

    return {
      metricFilter: {
        metricName: rawMetricFilter.name,
        threshold: rawMetricFilter.threshold.toString(),
        windowSize: rawMetricFilter.window_size,
        cmpOp:
          cmpOpOptions.find(op => op.value === rawMetricFilter.cmp_op) ??
          cmpOpOptions[0],
        aggOp: rawMetricFilter.agg_op,
      },
      runFilter: {
        isRunNameFilterEnabled: runNameFilter != null,
        runName: runNameFilter?.run?.displayName?.$regex,
        limitToMyRuns: usernameFilter != null,
        username: usernameFilter?.run?.username?.$eq,
      },
      // Autofocus doesn't make sense when viewing UI
      // It's a bit odd to set this when reading from data received from the server,
      // So potentially in the future we should split up the config and only return
      // metric/runFilter and then have a separate config for UI state.
      allowAutoFocus: false,
    };
  } catch (e) {
    console.error(
      'Failed to parse run metric config from trigger:',
      e,
      trigger
    );
    return undefined;
  }
};

/*
output:
run by me
run matching "run-name-.*"
run matching "run-name-.*" by me
*/
export const getRunFilterString = (runFilter: Partial<RunFilter>) => {
  const runNameSnippet = runFilter.isRunNameFilterEnabled
    ? `matching "${runFilter.runName}"`
    : '';
  const myRunsSnippet = runFilter.limitToMyRuns ? `by me` : '';
  if (runNameSnippet.length > 0 || myRunsSnippet.length > 0) {
    const str = [runNameSnippet, myRunsSnippet]
      .filter(s => s.length > 0)
      .join(' ');
    return `runs ${str} have an `;
  }
  return '';
};

export const getAggOpStr = (aggOp: string | undefined) => aggOp?.toLowerCase();
